import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO, generateWebPageSchema, generateOrganizationSchema } from '../../utils/seo';

interface Property {
  id: number;
  images: string[];
  title: string;
  price: number;
  location: string;
  propertyType: string;
  bedrooms: number;
  berth: number;
  distance: number;
  area: string;
  condition: string;
  description: string;
  features: string[];
}

export default function LeisureHomeSalesPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [distance, setDistance] = useState('');
  const [berth, setBerth] = useState('');
  const [propertyType, setPropertyType] = useState('all');
  const [bedrooms, setBedrooms] = useState('');
  const [area, setArea] = useState('');
  const [condition, setCondition] = useState('');
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showListingForm, setShowListingForm] = useState(false);
  const [listingFormData, setListingFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    telephone: '',
    propertyType: '',
    numberOfProperties: '',
    message: ''
  });
  const [isSubmittingListing, setIsSubmittingListing] = useState(false);
  const [listingSubmitStatus, setListingSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    customerName: '',
    address: '',
    telephone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [currentImageIndex, setCurrentImageIndex] = useState<{ [key: number]: number }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const propertiesPerPage = 6;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const properties: Property[] = [
    {
      id: 1,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e11f6f89c1e18f9e5ad59f12a35fad01.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/ec9ea709c0afb0519f0bc8f9a4619e20.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2ce5299af57f1c17c13cc170429fe7d2.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2cf9f18debf3a15367ed0089eb737dac.jpeg'
      ],
      title: 'ABI Ambleside Premier Lodge',
      price: 69950,
      location: 'Ingoldmells, Lincolnshire',
      propertyType: 'lodge',
      bedrooms: 2,
      berth: 6,
      distance: 1,
      area: 'Ingoldmells',
      condition: 'New',
      description: 'A spacious, luxury holiday lodge featuring contemporary open-plan living, premium integrated kitchen appliances, stylish interiors and outdoor decking — designed for year-round comfort and relaxed entertaining',
      features: ['Double Glazing', 'En-Suite Shower', 'Fully Furnished', 'Driveway Parking', 'Countryside Views', 'Exclusive Development', 'Waterside', 'Low Energy Costs']
    },
    {
      id: 2,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/c30c2c3c760cc9a8c1d68013e3f30226.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/c7c277625a388f7b7e701f77f66f7c65.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/1b7948d5ab66dbb305bd0104ca6db2c0.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2a7eddd40bc7005eb8d5df04a8702413.jpeg'
      ],
      title: 'ABI Coworth Deluxe',
      price: 35450,
      location: 'Ingoldmells, Lincolnshire',
      propertyType: 'static caravan',
      bedrooms: 2,
      berth: 6,
      distance: 1,
      area: 'Ingoldmells',
      condition: 'Good',
      description: 'A stylish 2022 ABI Cosworth Deluxe, offering premium craftsmanship, a spacious open-plan layout, and high-quality furnishings for comfortable holiday living.',
      features: ['Double Glazing', 'En-Suite Shower', 'Fully Furnished', 'Driveway Parking', 'Countryside Views', 'Exclusive Development', 'Fully Residential', 'Waterside', 'Low Energy Costs']
    },
    {
      id: 3,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/6f8a9a968df10b41022c03a776d47b5f.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/1e1bf90856d537cadeca69718df2631d.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/5e92bbbde0a0a5062cddbb1c83467902.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/3d57d8f485eef6b47e539eb15141ec1d.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/91566028a899a721605214acdac65abe.jpeg'
      ],
      title: 'ABI Beaumont Lodge',
      price: 56950,
      location: 'Ingoldmells, Lincolnshire',
      propertyType: 'static caravan',
      bedrooms: 3,
      berth: 8,
      distance: 1,
      area: 'Ingoldmells',
      condition: 'Good',
      description: 'A well-appointed 2020 ABI Beaumont Lodge combining elegant design, generous living space, and quality finishes for relaxed, luxurious holiday park living.',
      features: ['Full Wrap Around Decking', 'Patio Area', 'Double Glazing', 'Fully Furnished', 'Fully Residential', 'Close to Beach', 'Amazing Views', 'Tranquil Park', 'Waterside']
    },
    {
      id: 4,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/c44304d6037f1eec1178f95b1f36e4a6.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/6d7c89184cce1d8ab4f6e78cb8d90057.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/559f1c149bd93656b4f25da6cbd395fc.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/99c62cb911adc75e2a81d443bbedf7cd.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/bb7929e7952348c9b64c270903c9f8f5.jpeg'
      ],
      title: 'Willerby Malton',
      price: 39950,
      location: 'Sutton-on-Sea',
      propertyType: 'static caravan',
      bedrooms: 2,
      berth: 6,
      distance: 2,
      area: 'Sutton-on-Sea',
      condition: 'Good',
      description: 'A spacious 2-bedroom Willerby Malton static caravan offering comfortable family accommodation with modern interior, fully equipped kitchen, and attractive decking area for outdoor living.',
      features: ['2 Bedrooms', 'Sleeps 6', 'Decking', 'Fully Furnished', 'Double Glazing', 'Central Heating', 'Modern Kitchen', 'Family Bathroom', 'Close to Beach', 'Parking']
    },
    {
      id: 5,
      images: [
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/5e9dd5df316144dce00e5aa48a659187.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/53a32db37ca5958d68f59ef3cbabcfb0.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/eba40af4fe594a9e70a8a20031996100.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/7eaba741833aeafb14dfe649207a9ba6.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2b329b3eeed4a8f9f12c98f54d0e1ef4.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/fcc102934bf7ac18b6de24fbd669af76.jpeg',
        'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2a05b73f01a9800b6aeff8c90b8ea68a.jpeg'
      ],
      title: 'Willerby Manor',
      price: 54950,
      location: 'Sutton-on-Sea',
      propertyType: 'Static Caravan',
      bedrooms: 2,
      berth: 6,
      distance: 4,
      area: 'Sutton-on-Sea',
      condition: 'New',
      description: 'A brand new Willerby Manor with elegant styling, spacious living areas, and premium finishes designed for relaxed, luxurious holiday living',
      features: ['Waterside', 'Driveway Parking', 'Double Glazing', 'Exclusive Development', 'Dog Friendly Park', 'Close to the Beach', 'Fully Furnished', 'Full Wrap Around Decking', 'Great Owners Benefits']
    }
  ];

  const filteredProperties = properties.filter(property => {
    if (minPrice && property.price < parseInt(minPrice)) return false;
    if (maxPrice && property.price > parseInt(maxPrice)) return false;
    if (distance && property.distance > parseInt(distance)) return false;
    if (berth && property.berth < parseInt(berth)) return false;
    if (propertyType !== 'all' && property.propertyType !== propertyType) return false;
    if (bedrooms && property.bedrooms < parseInt(bedrooms)) return false;
    if (area && !property.area.toLowerCase().includes(area.toLowerCase())) return false;
    if (condition && property.condition.toLowerCase() !== condition.toLowerCase()) return false;
    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);
  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(indexOfFirstProperty, indexOfLastProperty);

  const handleReset = () => {
    setMinPrice('');
    setMaxPrice('');
    setDistance('');
    setBerth('');
    setPropertyType('all');
    setBedrooms('');
    setArea('');
    setCondition('');
    setCurrentPage(1);
  };

  const handleOpenEnquiry = (property: Property) => {
    setSelectedProperty(property);
    setShowEnquiryForm(true);
    setSubmitStatus('idle');
    document.body.style.overflow = 'hidden';
  };

  const handleCloseEnquiry = () => {
    setShowEnquiryForm(false);
    setSelectedProperty(null);
    setFormData({
      customerName: '',
      address: '',
      telephone: '',
      email: ''
    });
    setSubmitStatus('idle');
    document.body.style.overflow = 'auto';
  };

  const handleSubmitEnquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.customerName || !formData.address || !formData.telephone || !formData.email) {
      alert('Please fill in all fields');
      return;
    }

    if (!selectedProperty) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('customerName', formData.customerName);
      formBody.append('address', formData.address);
      formBody.append('telephone', formData.telephone);
      formBody.append('email', formData.email);
      formBody.append('propertyTitle', selectedProperty.title);
      formBody.append('propertyPrice', `£${selectedProperty.price.toLocaleString()}`);
      formBody.append('propertyLocation', selectedProperty.location);
      formBody.append('propertyType', selectedProperty.propertyType);
      formBody.append('recipientEmail', 'leisure@lincolnshireadworks.co.uk');

      const response = await fetch('https://readdy.ai/api/form/d5jbq2mihetfqh7m5njg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          handleCloseEnquiry();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenListingForm = () => {
    setShowListingForm(true);
    setListingSubmitStatus('idle');
    document.body.style.overflow = 'hidden';
  };

  const handleCloseListingForm = () => {
    setShowListingForm(false);
    setListingFormData({
      businessName: '',
      contactName: '',
      email: '',
      telephone: '',
      propertyType: '',
      numberOfProperties: '',
      message: ''
    });
    setListingSubmitStatus('idle');
    document.body.style.overflow = 'auto';
  };

  const handleSubmitListingForm = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!listingFormData.businessName || !listingFormData.contactName || !listingFormData.email || !listingFormData.telephone) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmittingListing(true);
    setListingSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('businessName', listingFormData.businessName);
      formBody.append('contactName', listingFormData.contactName);
      formBody.append('email', listingFormData.email);
      formBody.append('telephone', listingFormData.telephone);
      formBody.append('propertyType', listingFormData.propertyType);
      formBody.append('numberOfProperties', listingFormData.numberOfProperties);
      formBody.append('message', listingFormData.message);
      formBody.append('recipientEmail', 'leisure@lincolnshireadworks.co.uk');

      const response = await fetch('https://readdy.ai/api/form/listing-enquiry-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setListingSubmitStatus('success');
        setTimeout(() => {
          handleCloseListingForm();
        }, 2000);
      } else {
        setListingSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setListingSubmitStatus('error');
    } finally {
      setIsSubmittingListing(false);
    }
  };

  const handlePrevImage = (propertyId: number, totalImages: number) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [propertyId]: ((prev[propertyId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const handleNextImage = (propertyId: number, totalImages: number) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [propertyId]: ((prev[propertyId] || 0) + 1) % totalImages
    }));
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [minPrice, maxPrice, distance, berth, propertyType, bedrooms, area, condition]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const pageUrl = `${siteUrl}/leisure-home-sales`;

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      generateOrganizationSchema(),
      generateWebPageSchema(
        'Seaside Vouchers - Seaside Discounts & Offers | Free App',
        'Save money on your Lincolnshire seaside adventures with Seaside Vouchers. Free app with exclusive discounts on attractions, dining, shopping, accommodation and more. No subscription required.',
        pageUrl
      )
    ]
  };

  return (
    <>
      <SEO
        title="Seaside Vouchers - Seaside Discounts & Offers | Free App"
        description="Save money on your Lincolnshire seaside adventures with Seaside Vouchers. Free app with exclusive discounts on attractions, dining, shopping, accommodation and more. No subscription required."
        keywords="seaside vouchers, Lincolnshire coast, Skegness discounts, Mablethorpe offers, seaside deals, holiday savings, coastal attractions"
        canonical={pageUrl}
        ogImage="https://seasidevouchers.co.uk/wp-content/uploads/2024/11/Group-1000005662-1024x974.png"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img 
                  src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/659671df2896ffc0ce475e0437197503.png" 
                  alt="Seaside Vouchers" 
                  className="h-16 w-16"
                />
                <img 
                  src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e0d98287e8c9dc8abdecc58291257ecc.png" 
                  alt="SEASIDE VOUCHERS" 
                  className="h-12"
                />
              </Link>
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Home</Link>
                <Link to="/how-it-works" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">How it works</Link>
                <Link to="/seaside-offers" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Seaside Vouchers App</Link>
                <Link to="/voucher-book" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Voucher Book</Link>
                <Link to="/leisure-home-sales" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Leisure Home Sales</Link>
                <Link to="/accommodation" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Accommodation</Link>
                <Link to="/contact" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Contact us</Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-28 pb-8 bg-gradient-to-br from-cyan-400 to-cyan-500 overflow-hidden">
          {/* Animated Decorative Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-64 h-64 bg-cyan-300 rounded-full opacity-20 blur-3xl animate-pulse"></div>
            <div className="absolute bottom-10 right-20 w-80 h-80 bg-blue-300 rounded-full opacity-20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-white rounded-full opacity-10 blur-2xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="text-center text-white mb-6">
              <h1 className="text-5xl font-bold mb-3 drop-shadow-lg">Leisure Home Sales</h1>
              <p className="text-xl max-w-3xl mx-auto drop-shadow-md">Discover your perfect holiday home on the beautiful Lincolnshire Coast</p>
            </div>
          </div>
        </section>

        {/* Search Filters */}
        <section className="bg-white border-b border-gray-200 shadow-sm sticky top-[88px] z-40">
          <div className="container mx-auto px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">MIN PRICE</label>
                <input
                  type="number"
                  placeholder="£0"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">MAX PRICE</label>
                <input
                  type="number"
                  placeholder="£200,000"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">DISTANCE (miles)</label>
                <input
                  type="number"
                  placeholder="Any"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">BERTH</label>
                <input
                  type="number"
                  placeholder="Any"
                  value={berth}
                  onChange={(e) => setBerth(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">PROPERTY TYPE</label>
                <select
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer"
                >
                  <option value="all">All</option>
                  <option value="touring caravan">Touring Caravan</option>
                  <option value="static caravan">Static Caravan</option>
                  <option value="park home">Park Home</option>
                  <option value="lodge">Lodge</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">BEDROOMS</label>
                <input
                  type="number"
                  placeholder="Any"
                  value={bedrooms}
                  onChange={(e) => setBedrooms(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">AREA</label>
                <input
                  type="text"
                  placeholder="Location"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">CONDITION</label>
                <input
                  type="text"
                  placeholder="Any"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleReset}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Reset Filters
              </button>
              <p className="text-sm text-gray-600">
                <strong>{filteredProperties.length}</strong> properties found
              </p>
            </div>
          </div>
        </section>

        {/* Property Listings */}
        <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-6">
            {filteredProperties.length > 0 ? (
              <>
                <div className="space-y-6">
                  {currentProperties.map((property) => {
                    const currentIndex = currentImageIndex[property.id] || 0;
                    return (
                      <div key={property.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden border border-gray-200">
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-80 h-64 md:h-auto overflow-hidden flex-shrink-0 relative group">
                            <img 
                              src={property.images[currentIndex]} 
                              alt={`${property.title} - Image ${currentIndex + 1}`} 
                              className="w-full h-full object-cover object-top"
                            />
                            
                            {/* Previous Button */}
                            <button
                              onClick={() => handlePrevImage(property.id, property.images.length)}
                              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-full transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                              aria-label="Previous image"
                            >
                              <i className="ri-arrow-left-s-line text-2xl"></i>
                            </button>

                            {/* Next Button */}
                            <button
                              onClick={() => handleNextImage(property.id, property.images.length)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-full transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                              aria-label="Next image"
                            >
                              <i className="ri-arrow-right-s-line text-2xl"></i>
                            </button>

                            {/* Image Counter */}
                            <div className="absolute bottom-3 right-3 bg-black/70 text-white px-3 py-1 rounded-full text-xs font-medium">
                              {currentIndex + 1} / {property.images.length}
                            </div>

                            {/* Image Dots */}
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                              {property.images.map((_, index) => (
                                <button
                                  key={index}
                                  onClick={() => setCurrentImageIndex(prev => ({ ...prev, [property.id]: index }))}
                                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                                    index === currentIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/75'
                                  }`}
                                  aria-label={`Go to image ${index + 1}`}
                                />
                              ))}
                            </div>
                          </div>
                          
                          <div className="flex-1 p-6">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-1">{property.title}</h3>
                                <p className="text-sm text-gray-600 flex items-center gap-1">
                                  <i className="ri-map-pin-line"></i>
                                  {property.location}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-3xl font-bold text-cyan-600">£{property.price.toLocaleString()}</p>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-700">
                              <span className="flex items-center gap-1">
                                <i className="ri-home-4-line text-cyan-600"></i>
                                <strong>{property.propertyType}</strong>
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-hotel-bed-line text-cyan-600"></i>
                                <strong>{property.bedrooms}</strong> Bedrooms
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-group-line text-cyan-600"></i>
                                <strong>{property.berth}</strong> Berth
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-map-pin-2-line text-cyan-600"></i>
                                <strong>{property.distance}</strong> miles away
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-star-line text-cyan-600"></i>
                                <strong>{property.condition}</strong>
                              </span>
                            </div>

                            <p className="text-sm text-gray-700 mb-6">{property.description}</p>

                            <div className="flex flex-wrap gap-2 mb-4">
                              {property.features.map((feature, index) => (
                                <span 
                                  key={index}
                                  className="px-3 py-1 bg-cyan-50 text-cyan-700 text-xs font-medium rounded-full"
                                >
                                  {feature}
                                </span>
                              ))}
                            </div>

                            <div className="flex gap-3">
                              <Link 
                                to={`/property/${property.id}`}
                                className="px-6 py-2 bg-cyan-500 text-white text-sm font-medium rounded-md hover:bg-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
                              >
                                View Details
                              </Link>
                              <button 
                                onClick={() => handleOpenEnquiry(property)}
                                className="px-6 py-2 bg-orange-500 text-white text-sm font-medium rounded-md hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
                              >
                                Send Enquiry
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-12 flex items-center justify-center gap-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-s-line"></i> Previous
                    </button>

                    {/* Page Numbers */}
                    <div className="flex gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`w-10 h-10 flex items-center justify-center font-medium rounded-md transition-colors cursor-pointer ${
                            currentPage === pageNumber
                              ? 'bg-cyan-500 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      ))}
                    </div>

                    {/* Next Button */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      Next <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-home-smile-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No properties found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your search filters to see more results</p>
                <button
                  onClick={handleReset}
                  className="px-6 py-3 bg-cyan-500 text-white font-medium rounded-md hover:bg-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Reset All Filters
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-orange-500">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">Want to List Your Property?</h2>
            <p className="text-xl text-white mb-3">Sell your leisure home without paying hefty commission fees</p>
            <p className="text-lg text-white/95 mb-2 max-w-3xl mx-auto">
              Save thousands by listing with Seaside Vouchers - no expensive agent commissions
            </p>
            <p className="text-base text-white/90 mb-8 max-w-2xl mx-auto">
              Join parks and private sellers across the Lincolnshire Coast who choose our commission-free service to reach genuine buyers directly
            </p>
            
            <button
              onClick={handleOpenListingForm}
              className="inline-block px-8 py-4 bg-white text-orange-500 font-bold text-lg rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us Today
            </button>
          </div>
        </section>

        {/* Listing Enquiry Form Modal */}
        {showListingForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-900">List Your Property</h3>
                <button
                  onClick={handleCloseListingForm}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  Fill in the form below and we'll get back to you with information about listing your leisure home with Seaside Vouchers.
                </p>

                {/* Listing Form */}
                <form id="listing-enquiry-form" data-readdy-form onSubmit={handleSubmitListingForm} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Business/Park Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      value={listingFormData.businessName}
                      onChange={(e) => setListingFormData({ ...listingFormData, businessName: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      placeholder="Enter your business or park name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Contact Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="contactName"
                      value={listingFormData.contactName}
                      onChange={(e) => setListingFormData({ ...listingFormData, contactName: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={listingFormData.email}
                      onChange={(e) => setListingFormData({ ...listingFormData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Telephone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      value={listingFormData.telephone}
                      onChange={(e) => setListingFormData({ ...listingFormData, telephone: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Property Type
                    </label>
                    <select
                      name="propertyType"
                      value={listingFormData.propertyType}
                      onChange={(e) => setListingFormData({ ...listingFormData, propertyType: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm cursor-pointer"
                    >
                      <option value="">Select property type</option>
                      <option value="Static Caravan">Static Caravan</option>
                      <option value="Touring Caravan">Touring Caravan</option>
                      <option value="Park Home">Park Home</option>
                      <option value="Lodge">Lodge</option>
                      <option value="Multiple Types">Multiple Types</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Number of Properties to List
                    </label>
                    <input
                      type="text"
                      name="numberOfProperties"
                      value={listingFormData.numberOfProperties}
                      onChange={(e) => setListingFormData({ ...listingFormData, numberOfProperties: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      placeholder="e.g., 1, 5, 10+"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Information
                    </label>
                    <textarea
                      name="message"
                      value={listingFormData.message}
                      onChange={(e) => setListingFormData({ ...listingFormData, message: e.target.value })}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm resize-none"
                      placeholder="Tell us more about your properties or any questions you have"
                    />
                    <p className="text-xs text-gray-500 mt-1">{listingFormData.message.length}/500 characters</p>
                  </div>

                  {/* Status Messages */}
                  {listingSubmitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center gap-3">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                      <div>
                        <p className="font-semibold text-green-900">Enquiry Sent Successfully!</p>
                        <p className="text-sm text-green-700">We'll get back to you soon.</p>
                      </div>
                    </div>
                  )}

                  {listingSubmitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center gap-3">
                      <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                      <div>
                        <p className="font-semibold text-red-900">Submission Failed</p>
                        <p className="text-sm text-red-700">Please try again later.</p>
                      </div>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseListingForm}
                      className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingListing}
                      className="flex-1 px-6 py-3 bg-orange-500 text-white font-medium rounded-md hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmittingListing ? 'Sending...' : 'Submit Enquiry'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Enquiry Form Modal */}
        {showEnquiryForm && selectedProperty && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-900">Send Enquiry</h3>
                <button
                  onClick={handleCloseEnquiry}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6">
                {/* Property Details */}
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-4 mb-6 border border-cyan-200">
                  <h4 className="font-bold text-lg text-gray-900 mb-2">Property Details</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Title:</strong> {selectedProperty.title}</p>
                    <p><strong>Price:</strong> £{selectedProperty.price.toLocaleString()}</p>
                    <p><strong>Location:</strong> {selectedProperty.location}</p>
                    <p><strong>Type:</strong> {selectedProperty.propertyType}</p>
                  </div>
                </div>

                {/* Enquiry Form */}
                <form id="leisure-enquiry-form" data-readdy-form onSubmit={handleSubmitEnquiry} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Customer Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm resize-none"
                      placeholder="Enter your full address"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.address.length}/500 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Telephone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                      placeholder="Enter your email address"
                    />
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center gap-3">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                      <div>
                        <p className="font-semibold text-green-900">Enquiry Sent Successfully!</p>
                        <p className="text-sm text-green-700">We'll get back to you soon.</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center gap-3">
                      <i className="ri-error-warning-fill text-2xl text-red-600"></i>
                      <div>
                        <p className="font-semibold text-red-900">Submission Failed</p>
                        <p className="text-sm text-red-700">Please try again later.</p>
                      </div>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseEnquiry}
                      className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 px-6 py-3 bg-orange-500 text-white font-medium rounded-md hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="bg-cyan-500 text-white py-12">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
              <div>
                <div>
                  <img 
                    src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/659671df2896ffc0ce475e0437197503.png" 
                    alt="Seaside Vouchers" 
                    className="h-20 w-20 transform hover:scale-110 transition-transform duration-300"
                  />
                  <img 
                    src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e0d98287e8c9dc8abdecc58291257ecc.png" 
                    alt="SEASIDE VOUCHERS" 
                    className="h-10"
                  />
                </div>
              </div>

              <div>
                <h4 className="font-bold text-lg mb-4">Quick Link</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Home</Link></li>
                  <li><Link to="/how-it-works" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">How it works</Link></li>
                  <li><Link to="/seaside-offers" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Seaside offers</Link></li>
                  <li><Link to="/voucher-book" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Voucher Book</Link></li>
                  <li><Link to="/leisure-home-sales" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Leisure Home Sales</Link></li>
                  <li><Link to="/accommodation" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Accommodation</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-lg mb-4">Company</h4>
                <ul className="space-y-2 text-sm">
                  <li><a href="https://seasidevouchers.co.uk/privacy-policy/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Privacy &amp; cookie policy</a></li>
                  <li><a href="https://seasidevouchers.co.uk/terms-conditions/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">T&amp;C</a></li>
                  <li><Link to="/contact" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Contact us</Link></li>
                  <li><Link to="/business-registration" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Add Your Business</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-lg mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  <a href="https://www.facebook.com/people/seasidevouchers/61566031950810/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                    <i className="ri-facebook-fill text-xl"></i>
                  </a>
                  <a href="https://www.instagram.com/seaside_vouchers/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                    <i className="ri-instagram-line text-xl"></i>
                  </a>
                  <a href="https://www.tiktok.com/@seasidevouchers" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                    <i className="ri-tiktok-fill text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 pt-6 text-center text-sm">
              <p>Copyright © 2023 Seaside Voucher, All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
